/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  FilterOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { Checkbox, Popover } from 'antd'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import { font, weightSemiBold } from 'src/components/css'
import type { UseGridToolbarResult } from '../hooks/useGridToolbar'
import { gameViewsLabel } from '../utils/i18n'

export interface GridToolbarProps {
  toolbar: UseGridToolbarResult
  /** True while the sheet owns the whole window. */
  isFullscreen?: boolean
  /** Omitted by hosts that have no full-screen shell to toggle. */
  onToggleFullscreen?: () => void
}

/**
 * Search and column chooser — the reference grid's toolbar
 * (`ag-grid-demos/performance`), rebuilt on Community.
 *
 * The demo gets its column chooser and its filter panel from the Enterprise
 * `sideBar`, which we do not licence. Rebuilding the chooser over
 * `setColumnsVisible` / `resetColumnState` costs one popover and behaves the
 * same.
 *
 * The search box is the grid's ONLY filter UI — the per-column filter row the
 * demo carries is gone, because every other table in the app narrows from one
 * box above it. It runs AG Grid's quick filter across every column, so it
 * covers what a per-column filter would have.
 */
const GridToolbar = ({
  toolbar,
  isFullscreen = false,
  onToggleFullscreen,
}: GridToolbarProps) => {
  'use memo'

  const [columnsOpen, setColumnsOpen] = useState(false)

  const columnsPanel = (
    <div css={panelStyles} data-testid='game-details-columns-panel'>
      <p css={panelTitleStyles}>
        {capitalizeWords(gameViewsLabel('gameGridColumnsTitle'))}
      </p>
      <div css={panelListStyles}>
        {toolbar.columns.map((column) => (
          <Checkbox
            key={column.colId}
            checked={column.visible}
            onChange={(event) =>
              toolbar.setColumnVisible(column.colId, event.target.checked)
            }
          >
            <span css={checkboxLabelStyles}>{column.headerName}</span>
          </Checkbox>
        ))}
      </div>
      <div css={panelActionsStyles}>
        <button
          type='button'
          css={secondaryButtonStyles}
          data-testid='game-details-columns-reset'
          onClick={toolbar.resetColumns}
        >
          {capitalizeWords(gameViewsLabel('gameGridColumnsReset'))}
        </button>
      </div>
    </div>
  )

  return (
    <div css={toolbarStyles} data-testid='game-details-toolbar'>
      <label css={searchStyles}>
        <SearchOutlined aria-hidden='true' css={searchIconStyles} />
        <span css={visuallyHiddenStyles}>
          {gameViewsLabel('gameGridSearchLabel')}
        </span>
        <input
          type='text'
          css={searchInputStyles}
          value={toolbar.searchText}
          placeholder={gameViewsLabel('gameGridSearchPlaceholder')}
          onChange={(event) => toolbar.setSearchText(event.target.value)}
        />
      </label>

      {toolbar.isFiltered && (
        <button
          type='button'
          css={buttonStyles}
          data-testid='game-details-clear-filters'
          onClick={toolbar.clearFilters}
        >
          <FilterOutlined aria-hidden='true' />
          {capitalizeWords(gameViewsLabel('gameGridClearFilters'))}
        </button>
      )}

      <Popover
        open={columnsOpen}
        onOpenChange={setColumnsOpen}
        trigger='click'
        placement='bottomRight'
        content={columnsPanel}
      >
        <button
          type='button'
          css={buttonStyles}
          data-testid='game-details-columns'
          aria-expanded={columnsOpen}
          disabled={!toolbar.isReady}
        >
          <TableOutlined aria-hidden='true' />
          {capitalizeWords(gameViewsLabel('gameGridColumnsButton'))}
        </button>
      </Popover>

      {/*
        The way out of full screen has to be on screen, because the shell covers
        the nav, the tabs and the view switcher. So this button is the one
        control that keeps its label in both states rather than collapsing to an
        icon — leaving must never be a guess.
      */}
      {onToggleFullscreen !== undefined && (
        <button
          type='button'
          css={buttonStyles}
          data-testid='game-details-fullscreen'
          aria-pressed={isFullscreen}
          onClick={onToggleFullscreen}
        >
          {isFullscreen ? (
            <FullscreenExitOutlined aria-hidden='true' />
          ) : (
            <FullscreenOutlined aria-hidden='true' />
          )}
          {capitalizeWords(
            gameViewsLabel(
              isFullscreen
                ? 'gameGridFullscreenExit'
                : 'gameGridFullscreenEnter'
            )
          )}
        </button>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const toolbarStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  flex-wrap: wrap;
`

/**
 * The search box takes the slack so the buttons stay right-aligned at any width
 * — the same shape as the reference grid's toolbar.
 */
const searchStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1 1 220px;
  min-width: 180px;
  max-width: 420px;
`

const searchIconStyles = css`
  position: absolute;
  left: 11px;
  color: ${colors.NEUTRAL_50};
  pointer-events: none;
`

const searchInputStyles = css`
  ${font.body}
  width: 100%;
  height: 40px;
  padding: 0 11px 0 34px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_500};
  color: ${colors.NEUTRAL_0};
  font-size: 14px;

  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }
  &:focus {
    outline: none;
    border-color: ${colors.PRIMARY};
  }
`

const buttonStyles = css`
  ${font.body}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  font-size: 14px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.SECONDARY_300};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const panelStyles = css`
  ${font.body}
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
`

const panelTitleStyles = css`
  ${weightSemiBold}
  margin: 0;
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
`

const panelListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
`

const checkboxLabelStyles = css`
  font-size: 14px;
  color: ${colors.NEUTRAL_0};
`

const panelActionsStyles = css`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid ${colors.SECONDARY_200};
`

const secondaryButtonStyles = css`
  ${font.body}
  height: 32px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

/** Labels the search box for screen readers without printing a second label. */
const visuallyHiddenStyles = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export default GridToolbar
